import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { $getRoot, $insertNodes, type EditorState, type LexicalEditor } from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';
import mammoth from 'mammoth';

function Placeholder() {
  return (
    <div className="absolute top-3 left-4 text-foreground-400 text-sm font-body pointer-events-none select-none">
      Start writing your post content here...
    </div>
  );
}

function theme() {
  return {
    ltr: 'text-left',
    rtl: 'text-right',
    placeholder: 'text-foreground-400',
    paragraph: 'mb-3 text-foreground-950 font-body leading-relaxed text-sm',
    quote: 'border-l-4 border-accent-400 pl-4 italic text-foreground-700 my-4 text-sm',
    heading: {
      h1: 'font-heading text-2xl font-bold text-foreground-950 mb-3 mt-6',
      h2: 'font-heading text-xl font-bold text-foreground-950 mb-2 mt-5',
      h3: 'font-heading text-lg font-semibold text-foreground-950 mb-2 mt-4',
    },
    list: {
      nested: { listitem: 'ml-5' },
      ol: 'list-decimal ml-5 mb-3 text-foreground-950 font-body text-sm space-y-1',
      ul: 'list-disc ml-5 mb-3 text-foreground-950 font-body text-sm space-y-1',
      listitem: 'mb-1',
    },
    link: 'text-primary-500 underline hover:text-primary-700',
    text: {
      bold: 'font-bold',
      italic: 'italic',
      underline: 'underline',
      strikethrough: 'line-through',
    },
  };
}

function onError(error: Error) {
  console.error('Lexical editor error:', error);
}

interface RichTextEditorProps {
  initialHtml?: string;
  onChange: (html: string) => void;
}

function ToolbarPlugin() {
  return (
    <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-b border-background-200/70 bg-background-50">
      <div className="flex items-center gap-0.5 pr-2 border-r border-background-200/70 mr-2">
        <button
          type="button"
          onClick={() => document.execCommand('bold')}
          title="Bold"
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors font-bold"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => document.execCommand('italic')}
          title="Italic"
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors italic"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => document.execCommand('underline')}
          title="Underline"
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors underline"
        >
          U
        </button>
        <button
          type="button"
          onClick={() => document.execCommand('strikeThrough')}
          title="Strikethrough"
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors line-through"
        >
          S
        </button>
      </div>

      <div className="flex items-center gap-0.5 pr-2 border-r border-background-200/70 mr-2">
        <button
          type="button"
          onClick={() => document.execCommand('formatBlock', false, '<h2>')}
          title="Heading 2"
          className="w-8 h-8 flex items-center justify-center rounded text-xs text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors font-bold"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => document.execCommand('formatBlock', false, '<h3>')}
          title="Heading 3"
          className="w-8 h-8 flex items-center justify-center rounded text-xs text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors font-bold"
        >
          H3
        </button>
        <button
          type="button"
          onClick={() => document.execCommand('formatBlock', false, '<p>')}
          title="Paragraph"
          className="w-8 h-8 flex items-center justify-center rounded text-xs text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors"
        >
          P
        </button>
      </div>

      <div className="flex items-center gap-0.5 pr-2 border-r border-background-200/70 mr-2">
        <button
          type="button"
          onClick={() => document.execCommand('insertUnorderedList')}
          title="Bullet List"
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors"
        >
          <i className="ri-list-unordered" />
        </button>
        <button
          type="button"
          onClick={() => document.execCommand('insertOrderedList')}
          title="Numbered List"
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors"
        >
          <i className="ri-list-ordered" />
        </button>
      </div>

      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('Enter link URL:');
            if (url) document.execCommand('createLink', false, url);
          }}
          title="Insert Link"
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors"
        >
          <i className="ri-link" />
        </button>
        <button
          type="button"
          onClick={() => document.execCommand('insertHTML', false, '<blockquote class="border-l-4 border-accent-400 pl-4 italic text-foreground-700 my-4">Blockquote</blockquote>')}
          title="Blockquote"
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors"
        >
          <i className="ri-double-quotes-l" />
        </button>
      </div>
    </div>
  );
}

function LoadInitialContentPlugin({ initialHtml }: { initialHtml?: string }) {
  const [editor] = useLexicalComposerContext();
  const lastLoadedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!initialHtml || initialHtml === lastLoadedRef.current) return;

    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(initialHtml, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);

      const root = $getRoot();
      root.clear();
      $insertNodes(nodes);
    });

    lastLoadedRef.current = initialHtml;
  }, [editor, initialHtml]);

  return null;
}

function LexicalRichTextEditor({ initialHtml, onChange }: RichTextEditorProps) {
  const editorRef = useRef<LexicalEditor | null>(null);

  const initialConfig = {
    namespace: 'blog-editor',
    theme: theme(),
    onError,
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode],
  };

  const handleChange = (editorState: EditorState, editor: LexicalEditor) => {
    editorRef.current = editor;
    editorState.read(() => {
      const root = $getRoot();
      const html = root.getTextContent();
      onChange(html);
    });
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <LoadInitialContentPlugin initialHtml={initialHtml} />
      <div className="border border-background-300 rounded-lg overflow-hidden bg-background-50">
        <ToolbarPlugin />
        <div className="relative min-h-[300px] p-4">
          <RichTextPlugin
            contentEditable={<ContentEditable className="outline-none min-h-[280px] prose-clear" />}
            placeholder={<Placeholder />}
          />
        </div>
      </div>
      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      <OnChangePlugin onChange={handleChange} />
    </LexicalComposer>
  );
}

interface BlogEditorProps {
  initialTitle?: string;
  initialExcerpt?: string;
  initialContent?: string;
  initialCategory?: string;
  initialTags?: string[];
  initialIsPremium?: boolean;
  initialCoverImage?: string;
  onSave: (data: {
    title: string;
    excerpt: string;
    content: string;
    category: string;
    tags: string[];
    isPremium: boolean;
    coverImage: string;
  }) => void;
  previewUrl?: string | null;
  isSaving?: boolean;
}

export default function BlogEditor({
  initialTitle = '',
  initialExcerpt = '',
  initialContent = '',
  initialCategory = 'daily-tips',
  initialTags = [],
  initialIsPremium = false,
  initialCoverImage = '',
  onSave,
  previewUrl,
  isSaving = false,
}: BlogEditorProps) {
  const [title, setTitle] = useState(initialTitle);
  const [excerpt, setExcerpt] = useState(initialExcerpt);
  const [content, setContent] = useState(initialContent);
  const [contentHtml, setContentHtml] = useState(initialContent);
  const [category, setCategory] = useState(initialCategory);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>(initialTags);
  const [isPremium, setIsPremium] = useState(initialIsPremium);
  const [coverImage, setCoverImage] = useState(initialCoverImage);
  const [wordUploading, setWordUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleContentChange = (html: string) => {
    setContentHtml(html);
    setContent(html);
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleWordUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setWordUploading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setContent(result.value);
      setContentHtml(result.value);
      if (!title) {
        const name = file.name.replace(/\.(docx?|doc)$/i, '');
        setTitle(name.replace(/[-_]/g, ' '));
      }
    } catch {
      alert('Failed to parse the Word document. Please try again.');
    } finally {
      setWordUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCoverImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCoverImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      title,
      excerpt,
      content: contentHtml,
      category,
      tags,
      isPremium,
      coverImage,
    });
  };

  const categories = [
    { key: 'daily-tips', label: 'Daily Tips' },
    { key: 'mind-training', label: 'Mind Training' },
    { key: 'routines', label: 'Routines' },
    { key: 'nootropics', label: 'Nootropics' },
    { key: 'productivity', label: 'Productivity' },
    { key: 'wellness-science', label: 'Wellness Science' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <div>
        <label htmlFor="post-title" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
          Post Title
        </label>
        <input
          id="post-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter post title..."
          required
          className="w-full h-11 px-4 rounded-lg border border-background-300 bg-background-50 text-foreground-950 text-sm font-body placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
        />
      </div>

      {/* Category & Premium */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="post-category" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
            Category
          </label>
          <select
            id="post-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full h-11 px-4 rounded-lg border border-background-300 bg-background-50 text-foreground-950 text-sm font-body focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
          >
            {categories.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end pb-0.5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPremium}
              onChange={(e) => setIsPremium(e.target.checked)}
              className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-400"
            />
            <span className="text-sm font-label font-medium text-foreground-800">
              Premium Content <span className="text-foreground-500 font-normal">(members only)</span>
            </span>
          </label>
        </div>
      </div>

      {/* Excerpt */}
      <div>
        <label htmlFor="post-excerpt" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
          Excerpt
        </label>
        <textarea
          id="post-excerpt"
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          placeholder="A short summary of the post..."
          rows={2}
          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-950 text-sm font-body placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors resize-none"
        />
      </div>

      {/* Cover Image */}
      <div>
        <label className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
          Cover Image
        </label>
        {coverImage ? (
          <div className="relative rounded-lg overflow-hidden border border-background-200/70 mb-2">
            <img src={coverImage} alt="Cover" className="w-full h-48 object-cover object-top" />
            <button
              type="button"
              onClick={() => setCoverImage('')}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            className="w-full h-32 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-background-300 bg-background-50 hover:border-primary-300 hover:bg-primary-50/30 transition-colors text-foreground-500"
          >
            <i className="ri-image-add-line text-2xl" />
            <span className="text-sm font-body">Upload cover image</span>
            <span className="text-xs text-foreground-400 font-body">JPG, PNG, WebP &middot; Max 5MB</span>
          </button>
        )}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleCoverImageUpload}
          className="hidden"
        />
      </div>

      {/* Word Document Upload */}
      <div>
        <label className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
          Upload Word Document
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={wordUploading}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100 h-10 rounded-lg text-sm px-4"
          >
            {wordUploading ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Parsing...
              </>
            ) : (
              <>
                <i className="ri-file-word-2-line" />
                Upload .docx
              </>
            )}
          </button>
          <span className="text-xs text-foreground-500 font-body">
            Upload a Word document to populate the content below
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx,.doc"
          onChange={handleWordUpload}
          className="hidden"
        />
      </div>

      {/* Rich Text Editor */}
      <div>
        <label className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
          Content
        </label>
        <LexicalRichTextEditor initialHtml={initialContent} onChange={handleContentChange} />
        <p className="text-xs text-foreground-500 mt-1 font-body">
          Use the toolbar to format your text — bold, headings, lists, links, and more.
        </p>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
          Tags
        </label>
        <div className="flex items-center gap-2 mb-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            placeholder="Add a tag and press Enter..."
            className="flex-1 h-10 px-3 rounded-lg border border-background-300 bg-background-50 text-foreground-950 text-sm font-body placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
          />
          <button
            type="button"
            onClick={handleAddTag}
            className="inline-flex items-center justify-center gap-1 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 bg-secondary-500 text-background-50 hover:bg-secondary-600 h-10 rounded-lg text-sm px-4"
          >
            Add
          </button>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 bg-accent-100 text-accent-800 text-xs font-label px-2 py-1 rounded-full"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="hover:text-accent-900"
                >
                  <i className="ri-close-line" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-background-200/70">
        <button
          type="submit"
          disabled={isSaving || !title.trim()}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 bg-accent-500 text-foreground-950 hover:bg-accent-700 h-11 rounded-lg text-sm px-8 flex-1 sm:flex-none"
        >
          {isSaving ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line" />
              Save Post
            </>
          )}
        </button>
        {previewUrl ? (
          <Link
            to={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 border border-primary-300 bg-transparent text-primary-600 hover:bg-primary-50 h-11 rounded-lg text-sm px-8 flex-1 sm:flex-none"
          >
            <i className="ri-eye-line" />
            Preview
          </Link>
        ) : (
          <button
            type="button"
            disabled
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 border border-primary-300 bg-transparent text-primary-600 h-11 rounded-lg text-sm px-8 flex-1 sm:flex-none"
          >
            <i className="ri-eye-line" />
            Preview
          </button>
        )}
      </div>
    </form>
  );
}